import { useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

interface FAQItem {
  q: string;
  a: string;
}

interface ArticleLayoutProps {
  title1: string;
  title2?: string;
  title3?: string;
  date: string;
  readTime: string;
  tag: string;
  children: ReactNode;
  seoDescription?: string;
  faqs?: FAQItem[];
}

const SITE_URL = (typeof import.meta !== 'undefined' && (import.meta as Record<string, unknown>).env && ((import.meta as Record<string, unknown>).env as Record<string, string>).VITE_SITE_URL) || 'https://oopbuyspread.skin';

export default function ArticleLayout({ title1, title2, title3, date, readTime, tag, children, seoDescription, faqs }: ArticleLayoutProps) {
  const fullTitle = [title1, title2, title3].filter(Boolean).join(' | ');
  const headline = [title1, title2, title3].filter(Boolean).join(' – ');
  const description = seoDescription || `${headline}. Entdecke den besten OOPBUY Spreadsheet Guide auf Deutsch mit detaillierten Informationen, Tipps und Schritt-für-Schritt Anleitungen.`;
  const pagePath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    window.scrollTo(0, 0);

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": headline,
      "description": description,
      "datePublished": "2026-07-15",
      "dateModified": "2026-07-15",
      "author": {
        "@type": "Organization",
        "name": "OOPBUY Spreadsheet Team"
      },
      "publisher": {
        "@type": "Organization",
        "name": "OOPBUY Spreadsheet Guide"
      },
      "inLanguage": "de",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${SITE_URL}${pagePath}`
      }
    };

    const articleScript = document.createElement("script");
    articleScript.type = "application/ld+json";
    articleScript.textContent = JSON.stringify(articleSchema);
    document.head.appendChild(articleScript);

    let faqScript: HTMLScriptElement | null = null;

    if (faqs && faqs.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a
          }
        }))
      };

      faqScript = document.createElement("script");
      faqScript.type = "application/ld+json";
      faqScript.textContent = JSON.stringify(faqSchema);
      document.head.appendChild(faqScript);
    }

    return () => {
      if (articleScript.parentNode) articleScript.parentNode.removeChild(articleScript);
      if (faqScript && faqScript.parentNode) faqScript.parentNode.removeChild(faqScript);
    };
  }, [headline, description, pagePath, faqs]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <section className="relative pt-[64px] md:pt-[72px]">
          <div className="bg-foreground-950 py-20 md:py-28">
            <div className="max-w-[900px] mx-auto px-4 md:px-6 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-foreground-400 hover:text-white transition-colors text-sm mb-8 cursor-pointer"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                ← Zurück zur Startseite
              </Link>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-4">
                <span className="block">{title1}</span>
                {title2 && <span className="block">{title2}</span>}
                {title3 && <span className="block text-primary-400">{title3}</span>}
              </h1>
              <div className="flex flex-wrap items-center justify-center gap-3 mt-6 text-sm text-foreground-400">
                <span>{date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-500 hidden sm:block"></span>
                <span>OOPBUY Spreadsheet Team</span>
                <span className="w-1 h-1 rounded-full bg-foreground-500 hidden sm:block"></span>
                <span>Lesezeit: {readTime}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-500 hidden sm:block"></span>
                <span className="inline-block px-2 py-0.5 bg-primary-500/20 text-primary-300 rounded text-xs font-medium">{tag}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20">
          <article className="max-w-[720px] mx-auto px-4 md:px-6">
            <div className="prose-custom text-foreground-800 leading-relaxed space-y-6">
              {children}
            </div>
          </article>
        </section>

        <section className="relative py-16 md:py-24 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(https://readdy.ai/api/search-image?query=Modern%20streetwear%20fashion%20boutique%20display%20with%20organized%20clothing%20racks%20and%20colorful%20sneaker%20wall%2C%20warm%20ambient%20lighting%20from%20above%2C%20minimalist%20interior%20design%2C%20editorial%20fashion%20photography%20style%2C%20clean%20sophisticated%20retail%20atmosphere%2C%20earthy%20neutral%20tones%20with%20mint%20accents%2C%20high-end%20aesthetic%2C%20soft%20shadows&width=1920&height=600&seq=article-layout-cta-v2&orientation=landscape)`,
            }}
          />
          <div className="absolute inset-0 bg-black/55" />
          <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-4">
                  Bereit für OOPBUY Spreadsheet?
                </h2>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-full text-sm md:text-base hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Jetzt zum OOPBUY Spreadsheet
                  <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
              <div className="lg:text-right">
                <p className="text-white/80 text-sm">Jetzt alle Kategorien entdecken</p>
                <p className="text-white/80 text-sm">und schneller einkaufen.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}